import React, {useCallback} from 'react';
import {observer} from 'mobx-react';
import cx from 'classnames';
import {Title} from '@priority-software/priority-style-react';
import {Stepper} from '../../../components/Stepper';
import {Question} from './Question';
import {LottieLoader} from './Loader';
import {questionnaireStore} from '../../../store/OB_Questionnaire_Store';
import texts from '../../../texts';
import styles from './styles.module.scss';

type Props = {
    goToNextTab: () => void;
    stopQuiz: () => void;
    completed: boolean;
};

export const Quiz = observer(({goToNextTab, stopQuiz, completed}: Props) => {
    const {
        steps,
        activeStepID,
        goToStep,
        updateAnswerValue,
        goToNextStep,
        questionCategories,
        goToPrevStep,
        stepLoader,
        procedureLoading,
        finalizeQuiz,
        toggleStepSubQuestions,
        submittable
    } = questionnaireStore;

    const activeStep = steps[activeStepID];
    const lastCategoryIndex = questionCategories.length - 1;

    const stepBack = useCallback(
        (id: string) => {
            if (Number(id) > 0) {
                return goToPrevStep(id);
            }
            return stopQuiz();
        },
        [stopQuiz, goToPrevStep]
    );

    if (procedureLoading) return <LottieLoader />;

    if (!activeStep && !completed) return null;

    const questions = activeStep.questions;

    return (
        <div className={cx(styles.component, {[styles.disabledView]: stepLoader})}>
            <div className="tabHeader">
                <Title skin="h2" textColor="primary-500" componentClasses="tabTitle">
                    {texts.general_info_tab_title}
                </Title>

                <Stepper onClick={goToStep} steps={steps} activeStep={activeStepID} />
            </div>

            {activeStep.type === 'question' && (
                <Question
                    key={activeStepID}
                    data={questions}
                    subjectId={activeStepID}
                    isLoading={stepLoader}
                    toggleStepSubQuestions={toggleStepSubQuestions}
                    finalizeQuiz={finalizeQuiz}
                    showSubQuestion={activeStep.semiFilled}
                    onChange={updateAnswerValue}
                    onNextClick={goToNextStep}
                    onPrevClick={stepBack}
                    isLastSubject={lastCategoryIndex === Number(activeStepID)}
                    submittable={submittable}
                />
            )}
        </div>
    );
});
